import { Button } from '@systemeio/components';
import { BadRequest, InternalError } from '@systemeio/util';
import React, { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { Loader, Modal } from 'shared/components';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useMediaQuery from 'shared/hooks/use-media-query';
import { deepCompare } from 'shared/utils/deepCompare';
import { formatStrWithCapitalFirstLetter } from 'shared/utils/format-string-with-capital-first-letter';
import { FieldErrorAndDescription } from './form/field-error-and-description';
import { toast } from './toast';
export type CreateDataType<T> = { [Property in keyof T]: T[Property] };
type PropUpdate<T> = { [K in keyof T]: {
  field: K;
  onRender: (value: T[K], onChange: (value: T[K]) => void, state: T, onChangeState: (state: T) => void) => JSX.Element;
  shouldRender?: (state: CreateDataType<T>) => boolean;
} }[keyof T];
export type CreatableItemType<T> = PropUpdate<T> | PropUpdate<T>[];
export interface CreateModalProps<T> {
  opened: boolean;
  onClose: () => void;
  afterLeave?: () => void;
  defaultValues: T;
  caption?: string;
  fullCaption?: string;
  buttonCaption?: string;
  additionalInfo?: string | JSX.Element;
  modalDescription?: React.ReactNode;
  commonError?: string;
  disableErrorToast?: (error: unknown) => boolean;
  needToast?: boolean;
  toastCaption?: string | JSX.Element;
  creatable: CreatableItemType<T>[];
  onCreate: (data: CreateDataType<T>) => Promise<void>;
  isPrefetching?: boolean;
  requiredFields: Array<keyof T>;
  saveDataAttribute?: string;
  dataTestKey?: string;
}
const title = 'global.create';
function CreateModal<T extends CreateDataType<T>>({
  opened,
  onClose,
  afterLeave,
  creatable,
  caption,
  fullCaption,
  buttonCaption,
  additionalInfo,
  modalDescription,
  commonError,
  disableErrorToast,
  needToast = true,
  toastCaption,
  onCreate,
  defaultValues,
  isPrefetching,
  requiredFields,
  saveDataAttribute,
  dataTestKey
}: CreateModalProps<T>) {
  const {
    t
  } = useLocoTranslation();
  const [error, setError] = useState('');
  const errorMessage = error || commonError || '';
  const {
    isMobile,
    isTablet
  } = useMediaQuery();
  const isCompactViewport = isMobile || isTablet;
  const [required, setRequired] = useState(requiredFields);
  const [isDisabled, setIsDisabled] = useState(true);
  const initValues = useRef(defaultValues);
  const creatableRef = useRef(creatable);
  const setDefaultValues = useCallback((): T => {
    const obj: T = {} as T;
    creatableRef.current.forEach(value => {
      if (Array.isArray(value)) {
        value.forEach(el => {
          obj[el.field] = defaultValues[el.field];
        });
      } else {
        obj[value.field] = defaultValues[value.field];
      }
    });
    return obj;
  }, [defaultValues]);
  const [tempState, setTempState] = useState<T>(setDefaultValues());
  useEffect(() => {
    setTempState(setDefaultValues());
  }, [setDefaultValues]);
  useEffect(() => {
    setIsDisabled(!required.every(field => Number.isNaN(initValues.current[field]) ? !Number.isNaN(tempState[field]) : !deepCompare(tempState[field], initValues.current[field])));
  }, [required, tempState]);
  useEffect(() => {
    const tempArr: (keyof T)[] = [];
    creatableRef.current.forEach(value_0 => {
      if (Array.isArray(value_0)) {
        value_0.forEach(el_0 => {
          const isRequired = el_0.shouldRender ? el_0.shouldRender(tempState as CreateDataType<T>) : requiredFields.includes(el_0.field);
          isRequired && tempArr.push(el_0.field);
        });
      } else {
        const isRequired_0 = value_0.shouldRender ? value_0.shouldRender(tempState as CreateDataType<T>) : requiredFields.includes(value_0.field);
        isRequired_0 && tempArr.push(value_0.field);
      }
    });
    setRequired(tempArr);
  }, [requiredFields, tempState]);
  const clearModal = () => {
    setTempState(setDefaultValues());
    setError('');
  };
  const [isFetching, setIsFetching] = useState(false);
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      setError('');
      const data = Object.keys(tempState).filter(key => required.includes(key as keyof T) || tempState[key as keyof T] !== defaultValues[key as keyof T]).reduce((cur, key_0) => {
        return Object.assign(cur, {
          [key_0]: tempState[key_0 as keyof T]
        });
      }, {});
      setIsFetching(true);
      await onCreate(data as CreateDataType<T>);
      setIsFetching(false);
      onClose();
      needToast && toast.success(toastCaption ?? t('dashboard.actions.created', {
        module: caption
      }));
    } catch (e) {
      setIsFetching(false);
      if (e instanceof BadRequest) {
        if (e.errors.common) {
          setError(e.errors.common.join('<br />'));
        }
      } else if (e instanceof InternalError) {
        setError(t('core.error.internal_error_message'));
      } else {
        !disableErrorToast?.(e) && toast.error(t('global.error'));
      }
    }
  };
  return <>
      <Modal opened={opened} onClose={onClose} title={`${fullCaption ? fullCaption : caption ? formatStrWithCapitalFirstLetter(t('dashboard.actions.create', {
      module: caption
    })) : t(title)}`} isFetching={isFetching} afterLeave={() => {
      clearModal();
      afterLeave && afterLeave();
    }} data-test={dataTestKey ? `${dataTestKey}-create-modal` : 'create-modal'} render={<form onSubmit={handleSubmit} />} footer={<>
            <Button type="button" className="tablet:flex-none flex-1" onClick={onClose} disabled={isFetching} variant="secondary" size="md">
              {t('global.cancel')}
            </Button>
            <Button disabled={isDisabled} className="tablet:flex-none flex-1" type="submit" data-test={dataTestKey ? `${dataTestKey}-create-modal-submit-button` : 'create-modal-submit-button'} {...saveDataAttribute ? {
        ['data-test-element']: saveDataAttribute
      } : {}} isLoading={isFetching} variant="primary" size="md">
              {buttonCaption ? buttonCaption : t('global.create')}
            </Button>
          </>}>
        <div className="flex flex-col gap-4">
          {isPrefetching ? <Loader className="mx-auto" /> : creatable.map((created, idx) => {
          if (Array.isArray(created)) {
            return isCompactViewport ? created.map(el_1 => (el_1.shouldRender ? el_1.shouldRender(tempState as CreateDataType<T>) : true) && el_1.onRender(tempState[el_1.field], value_1 => {
              setTempState(prev => ({
                ...prev,
                [el_1.field]: value_1
              }));
            }, tempState, newState => setTempState(newState))) : <div key={`${idx}-wrapper`} className="flex items-start justify-between gap-4 [&>*]:flex-1">
                    {created.map(el_2 => (el_2.shouldRender ? el_2.shouldRender(tempState as CreateDataType<T>) : true) && el_2.onRender(tempState[el_2.field], value_2 => {
                setTempState(prev_0 => ({
                  ...prev_0,
                  [el_2.field]: value_2
                }));
              }, tempState, newState_0 => setTempState(newState_0)))}
                  </div>;
          } else {
            return (created.shouldRender ? created.shouldRender(tempState as CreateDataType<T>) : true) && created.onRender(tempState[created.field], value_3 => {
              setTempState(prev_1 => ({
                ...prev_1,
                [created.field]: value_3
              }));
            }, tempState, newState_1 => setTempState(newState_1));
          }
        })}
        </div>
        {!!modalDescription && modalDescription}
        <FieldErrorAndDescription error={errorMessage} errorClassName={'text-center'} description={additionalInfo} descriptionClassName={'flex w-full justify-center'} />
      </Modal>
    </>;
}
export default CreateModal;